import { createContext, useContext } from 'react';
import { usePinLock } from '../hooks/usePinLock';
import type { PinLockState } from '../hooks/usePinLock';
import { useAuth } from './AuthContext';

const PinLockContext = createContext<PinLockState | null>(null);

export const PinLockProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, profile, refreshProfile } = useAuth();

  const pinLock = usePinLock({
    user,
    pinEnabled: profile?.pin_enabled ?? false,
    pinHash: profile?.pin_hash ?? null,
    // Default: 5 minutes — keeps security tight out of the box
    pinLockTimeoutMinutes: profile?.pin_lock_timeout_minutes ?? 5,
    onProfileUpdate: refreshProfile,
    // profileLoaded: true only after the full profile object is available
    // This prevents the initialization effect from running with stale data
    profileLoaded: profile !== null,
  });

  return (
    <PinLockContext.Provider value={pinLock}>
      {children}
    </PinLockContext.Provider>
  );
};

export const usePinLockContext = (): PinLockState => {
  const ctx = useContext(PinLockContext);
  if (!ctx) {
    // Safe no-op default when used outside provider (e.g. admin pages)
    return {
      isLocked: false,
      isPinEnabled: false,
      pinCheckComplete: true, // don't block rendering outside app layout
      timeoutMinutes: 5,
      attempts: 0,
      verifyPin: async () => false,
      activatePin: async () => {},
      deactivatePin: async () => {},
      changePin: async () => false,
      updateTimeout: async () => {},
      lock: () => {},
      pinError: '',
    };
  }
  return ctx;
};
